import React, { useEffect, useMemo, useState } from "react";
import {
  FormSections,
  ProgramContainer,
} from "../../entities/formTemplateDataType";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import axios from "axios";
import DataUtils from "../../utilities/DataUtils";
import ReCaptchaV3 from "../captcha/CaptchComponent";
import ReCaptchaButton from "../captcha/captcha-button";
import { getCxpMessages } from "../../services/CxpProfileService";
import Utils from "../../utilities/Utils";
import SectionUtils from "../../utilities/SectionUtils";
import { getEnvVar } from "../../config/RuntimeConfig";
import TrackTaskCompletion, {
  getSubmissionId,
} from "../../utilities/TrackTaskCompletion";

interface Props {
  token: string;
  programContainer: ProgramContainer;
  isEng: boolean;
  section: FormSections;
  onButtonClick: (data) => void; // Callback function type
  callback?: (p: ProgramContainer) => void;
}

const CHCHandleSubmitComponent: React.FC<Props> = ({
  programContainer,
  isEng,
  section,
  onButtonClick,
  callback,
}) => {
  const [validationErrMsg, setValidationErrMsg] = useState<any[]>([]);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [confirmationVisible, setConfirmationVisible] =
    useState<boolean>(false);
  const [summaryVisible, setSummaryVisible] = useState<boolean>(true);
  const [transactionId, setTransactionId] = useState<string>("");
  const [uploadReceiptSuccess, setUploadReceiptSuccess] =
    useState<boolean>(false);
  const [renewalResponse, setRenewalResponse] = useState<any>({});
  const [submissionError, setSubmissionError] = useState<boolean>(false);

  const cleanupAfterSubmit = () => {
    if (!programContainer.ClearFormAfterSubmit) {
      return;
    }
    DataUtils.clearFormValue(programContainer);
    SectionUtils.resetDirtyFlags(programContainer);
  };

  const updateErrorResponse = (
    data: any,
    isOverrideMessage?: boolean,
    surveyLinkParams?: any,
  ) => {
    let newContainer = { ...programContainer } as ProgramContainer;
    const errorSection = newContainer?.ListOfFormSections?.find(
      (formSection) => formSection?.IsTechErrorPage,
    );
    if (errorSection) {
      errorSection.apiResponse = data;
      const errorElement = errorSection.ListOfFormSubSections.flatMap(
        (subSection) => subSection?.ListOfFormSectionElements || [],
      ).find((element) => element?.LoadBindName === "errorMessageContent");

      if (errorElement && isOverrideMessage) {
        errorElement.Value = data?.messageEn;
        errorElement.ValueFRA = data?.messageFr;
      }
      console.debug("Error in ODR Handle Submit:", data);
    }

    const surveyUrlParams =
      newContainer.ListOfFormSections[2].ListOfFormSubSections[0]
        .ListOfFormSectionElements[3].Value;
    newContainer.ListOfFormSections[2].ListOfFormSubSections[0].ListOfFormSectionElements[3].Value =
      {
        ...surveyUrlParams,
        ...surveyLinkParams,
        ...(Utils.isNotNullOrEmpty(data?.errorCode) && {
          code: data.errorCode,
        }),
        sts: "400",
      };
    callback(newContainer);
  };

  const shouldOverrideMessage = (response: any): boolean => {
    return (
      response?.status === 200 &&
      response?.data?.isHardstop &&
      response?.data?.errorCode?.length > 0
    );
  };

  const handleSubmit = (token: string) => {
    let invalidControlList = FormValidationUtils.invalidControls(section);

    if (invalidControlList.filter((p) => p.Datatype !== "Address").length > 0) {
      const errs = invalidControlList.filter((p) => p.Datatype !== "Address");
      setValidationErrMsg(errs);
      window.scrollTo(0, 0);
    } else {
      let url = programContainer.APIBasePath + programContainer.UpdateAPI;
      const data = DataUtils.collectFormValue(programContainer);

      const payload = {
        ...data,
        language: isEng ? "EN" : "FR",
        token,
        userId: programContainer.Profile?.userId,
      };

      setIsBusy(true);

      const surveyUrlParams = {
        cxpuser: programContainer.Profile?.userId ? "yes" : "no",
        svc: data?.childAgeCategory === "CAT-3" ? "unval" : "val",
        src: "link",
        sglocale: isEng ? "en" : "fr-ca",
      };

      axios
        .post(url, payload)
        .then((res) => {
          if (res?.status !== 200 || res?.data?.errorCode?.length > 0) {
            updateErrorResponse(
              res?.data,
              shouldOverrideMessage(res),
              surveyUrlParams,
            );

            setSummaryVisible(false);
            setSubmissionError(true);
            setIsBusy(false);
            return;
          }
          setConfirmationVisible(true);
          setSummaryVisible(false);
          setTransactionId(res?.data?.transactionId);
          setUploadReceiptSuccess(res?.data?.uploadReceiptSuccess);

          TrackTaskCompletion(programContainer, {
            serviceName: "child health card renewal",
            serviceItems: "health card",
            serviceAction: "renewal",
            value: "0.00",
            submissionId: getSubmissionId("chc"),
          });

          setRenewalResponse(res?.data);
          DataUtils.clearDraft(
            programContainer.CategoryId,
            programContainer.Profile?.email,
            programContainer.draftId,
          );
          programContainer.draftId = undefined;
          cleanupAfterSubmit();

          if (data.email) {
            const emailPayload = {
              to: data.email,
              transactionId: res?.data?.transactionId,
              language: isEng ? "EN" : "FR",
              category: data?.childAgeCategory || "",
              expireDate: data?.childHealthCardExpiryDate || "",
              hcVersion: data?.childCardNumber.slice(-2) || "",
              svcToken: res?.data?.svcToken || data?.svcToken || "",
            };
            const emailUrl = getEnvVar("EMAIL_URL");
            axios
              .post(emailUrl, emailPayload)
              .then(() => {})
              .catch((ex) => {
                console.log(ex);
              })
              .finally(() => {
                setIsBusy(false);
              });
          }

          if (
            programContainer.Profile?.userId &&
            programContainer.Profile?.token
          ) {
            getCxpMessages(programContainer.Profile?.token, {
              pageNumber: 1,
              pageSize: 20,
              search: "",
            })
              .then((messages) => {
                console.log("fetch message after subimtting:", messages);
                if (messages?.messages) {
                  const unread1 =
                    messages.messages.filter((msg) => !msg.isRead).length ?? 0;
                  const unread2 =
                    programContainer.Profile?.messages?.filter(
                      (msg) => !msg.isRead,
                    ).length ?? 0;
                  if (unread1 !== unread2) {
                    programContainer.Profile.messages = messages.messages;
                    callback(programContainer);
                  }
                }
              })
              .catch((ex) => {
                console.log("fetch message after subimtting failer:", ex);
              })
              .finally(() => {
                setIsBusy(false);
              });
          }
        })
        .catch((err) => {
          setIsBusy(false);

          updateErrorResponse(
            err.response?.data,
            shouldOverrideMessage(err?.response),
            surveyUrlParams,
          );

          setSummaryVisible(false);
          setSubmissionError(true);
          console.log(err);
        })
        .finally(() => {
          setIsBusy(false);
        });
    }
  };

  const props = useMemo(
    () => ({
      isBusy,
      confirmationVisible,
      summaryVisible,
      transactionId,
      uploadReceiptSuccess,
      renewalResponse,
      submissionError,
      validationErrMsg,
    }),
    [
      isBusy,
      confirmationVisible,
      summaryVisible,
      transactionId,
      uploadReceiptSuccess,
      renewalResponse,
      submissionError,
      validationErrMsg,
    ],
  );

  useEffect(() => {
    onButtonClick(props);
  }, [props]);

  return (
    <div className="ontario-margin-top-24-!">
      <ReCaptchaV3 lang={isEng ? "en" : "fr"}>
        <ReCaptchaButton
          key={"childHealthCardSubmit"}
          title={isEng ? "Submit" : "Soumettre"}
          action="submitCHC"
          buttonHandler={handleSubmit}
        >
          {isEng ? "Submit" : "Soumettre"}
        </ReCaptchaButton>
      </ReCaptchaV3>
    </div>
  );
};

export default CHCHandleSubmitComponent;
